<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="700">
        <v-form class="mb-3" validate-on="input lazy" @submit.prevent="updateUser">
            <v-card style="position: relative;">
                <v-card-title id="heading" class="d-flex justify-space-between">
                    <h4>Update User</h4>
                    <v-btn density="compact" icon="mdi-close" variant="flat" @click="$emit('update:modelValue',false)"/>
                </v-card-title>
                <v-overlay :model-value="!formReady" contained class="align-center justify-center">
                    <v-progress-circular indeterminate color="primary"/>
                </v-overlay>

                <v-container>
                    <v-row>
                        <v-col class="text-center" cols="12">
                            <UserAvatar :src="avatar" :first-name="formData['first_name']"
                                        :last-name="formData['last_name']" :email="formData['email']" size="150"/>
                            <v-btn flat icon size="small" @click="$refs.imageHandle.click()">
                                <v-icon icon="mdi-pencil"></v-icon>
                            </v-btn>
                            <v-file-input ref="imageHandle" v-model="formData['image']" accept="image/*"
                                          class="d-none" @change="avatarPreview"/>
                            <p class="font-weight-bold mt-2 me-3">{{ user.email }}</p>
                        </v-col>
                    </v-row>
                    <v-alert v-if="errors.length" class="mb-3 bg-error" closable close-label="Close Alert"
                             density="compact" variant="tonal">
                        <p v-for="error in errors">{{ error }}</p>
                    </v-alert>
                    <v-row>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['first_name']" autofocus
                                          :rules="[vRules.required]" density="compact" label="First Name"/>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['middle_name']" density="compact" label="Middle Name"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['last_name']"
                                          :rules="[vRules.required]" density="compact" label="Last Name"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['email']" :rules="[vRules.required, vRules.email]"
                                          density="compact" label="Email"/>
                        </v-col>

                        <v-col v-if="authUser.role?.toLowerCase().includes('admin')" cols="12"
                               sm="6">
                            <v-select v-model="formData['role']"
                                      :items="roles"
                                      :rules="user.type!=='admin'?[vRules.required]:[]"
                                      density="compact"
                                      item-value="id"
                                      item-title="title"
                                      label="User Role"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-select v-model="formData['timezone']" :items="timezones"
                                      :rules="[vRules.required]" density="compact" item-title="title"
                                      item-value="value" label="Timezone">
                            </v-select>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['password']" autocomplete="new-password" density="compact" label="Password"
                                          type="password"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['password_confirmation']" autocomplete="new-password" density="compact"
                                          label="Confirm Password"
                                          type="password"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['contact_phone']" density="compact" label="Contact Phone"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['mobile_phone']" density="compact" label="Mobile Phone"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['addr_1']" density="compact" label="Address 1"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['addr_2']" density="compact" label="Address 2"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['addr_district']" density="compact" label="District"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['addr_city']" density="compact" label="City"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['addr_state']" density="compact" label="State"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['addr_postalcode']" density="compact" label="Postal Code"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['addr_country']" density="compact" label="Country"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-select v-model="formData['status']" :items="['disabled','registered','verified']"
                                      :rules="[vRules.required]" density="compact" label="Status"/>
                        </v-col>
                    </v-row>
                </v-container>

                <v-card-actions class="px-4 d-flex justify-end">
                    <v-btn size="small" type="button"
                           @click="$emit('update:modelValue',false)">
                        Close
                    </v-btn>
                    <v-btn color="primary" size="small" type="submit">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-form>
    </v-dialog>
</template>

<script setup>
import {ref, reactive, onMounted} from "vue";
import {send} from "@/composables/apiClient";
import {useAuthStore} from "@/stores/authStore";
import timezones from "@/assets/js/timezones.json";
import {useSnackbarStore} from "@/stores/snackBarStore";
import UserAvatar from "@/components/UserAvatar.vue";

const formReady = ref(false);
const authUser = useAuthStore().user;
const snackbar = useSnackbarStore();
const props = defineProps({
    modelValue: Boolean,
    user: {
        type: Object
    }
});

const formData = reactive({
    valid: false,
    '_method': 'PUT',
    first_name: props.user.first_name ?? null,
    middle_name: props.user.middle_name ?? null,
    last_name: props.user.last_name ?? null,
    timezone: props.user.timezone ?? null,
    password: props.user.password ?? null,
    password_confirmation: props.user.password_confirmation ?? null,
    type: props.user.type ?? null,
    email: props.user.email ?? null,
    phone: props.user.phone ?? null,
    addr_1: props.user.addr_1 ?? null,
    addr_2: props.user.addr_2 ?? null,
    addr_district: props.user.addr_district ?? null,
    addr_city: props.user.addr_city ?? null,
    addr_state: props.user.addr_state ?? null,
    addr_postalcode: props.user.addr_postalcode ?? null,
    addr_country: props.user.addr_country ?? null,
    contact_phone: props.user.contact_phone ?? null,
    mobile_phone: props.user.mobile_phone ?? null,
    role: props.user?.role_id ?? props.user?.pivot?.role_id ?? null,
    company_id: props.user?.company_id ?? props.user?.pivot?.company_id ?? null,
    status: props.user.status ?? null,
});
const emits = defineEmits(['update:modelValue', 'updateUser']);
const dialogSwitch = ref(props.modelValue);
const avatar = ref(props.user.avatar);
const errors = ref([]);
const roles = ref([]);

onMounted(() => {
    Promise.all([fetchRoles()]).finally(() => formReady.value = true);
})
const fetchRoles = () => {
    return send('get', `roles`, {type: props.user.type})
        .then((response) => {
            roles.value = response.data.data;
        })
        .catch((error) => {
            console.log(error)
            snackbar.showSnackbar('Roles could not be loaded', 'error')
        })
}
const avatarPreview = () => {
    avatar.value = URL.createObjectURL(formData['image']);
}
const updateUser = () => {
    errors.value = [];
    if (formData['password']) {
        if (formData['password'].length < 8) {
            errors.value.push("Password must be at least 8 characters");
            document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
            return;
        }
        if (!formData['password_confirmation']) {
            errors.value.push("Please fill Confirm Password field");
            document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
            return;
        }
        if (formData['password'] !== formData['password_confirmation']) {
            errors.value.push("Password & Confirm Password mismatched");
            document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
            return;
        }
    }
    send('post', `users/${props.user.id}`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    }).then((response) => {
        snackbar.showSnackbar(response.data.message);
        if (response.data.success) {
            emits('updateUser', response.data.data.user);
            emits('update:modelValue', false);
        }
    }).catch(function (errorResponse) {
        if (errorResponse.response.data.errors) {
            let validationErrors = errorResponse.response.data.errors;
            Object.keys(validationErrors).forEach((key) => {
                errors.value.push(validationErrors[key][0]);
            });
        } else if (errorResponse.response.data.message) {
            errors.value.push(errorResponse.response.data.message);
        }
        snackbar.showSnackbar('Something went wrong', 'error');
        document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
    })
}

</script>
